import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { PatternMaker } from "@/components/height-map/PatternMaker";
import {
  FlaskConical,
  Image,
  Layers3,
  LayoutPanelTop,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const SettingsDialog = ({
  open,
  onOpenChange,
  settings,
  onChange,
  customPatterns = [],
  onSavePattern,
  onDeletePattern,
}) => {
  const update = (patch) => onChange({ ...settings, ...patch });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="settings-dialog"
        className="max-w-xl bg-[#0f1015] border-zinc-800 max-h-[88vh] overflow-y-auto scroll-thin p-0"
      >
        <DialogHeader className="sticky top-0 z-10 border-b border-zinc-800/80 bg-[#0f1015]/95 px-6 py-5 backdrop-blur-xl">
          <DialogTitle className="text-zinc-100">Workspace settings</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Tune the interface, previews, and optional creation tools.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-6 py-5">
          <SettingsSection
            icon={<LayoutPanelTop className="h-4 w-4" />}
            title="Interface"
            description="Choose your workspace layout and overall density."
          >
            <Row
              label="Original UI"
              badge="Classic"
              hint="Switch back to the original scrolling layout. Improved UI is now the default."
            >
              <Switch
                data-testid="setting-original-ui"
                checked={!!settings.originalUI}
                onCheckedChange={(v) => update({ originalUI: v })}
              />
            </Row>
            <Row label="Big viewports" hint="Give the height maps more screen space.">
              <Switch
                data-testid="setting-big-viewports"
                checked={settings.bigViewports}
                onCheckedChange={(v) => update({ bigViewports: v })}
              />
            </Row>
            <Row label="Show “How it works”" hint="Keep the guidance panel visible in the original UI.">
              <Switch
                data-testid="setting-show-how"
                checked={settings.showHow}
                onCheckedChange={(v) => update({ showHow: v })}
              />
            </Row>
          </SettingsSection>

          <SettingsSection
            icon={<Layers3 className="h-4 w-4" />}
            title="Height layers"
            description="Control the layer list and compact palette summary."
          >
            <div className="space-y-3 rounded-xl border border-zinc-800/70 bg-[#111217] p-4">
              <div className="flex items-center justify-between">
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span data-testid="setting-layer-height-label" className="cursor-help text-sm font-medium text-zinc-100">
                        Layer list height
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      data-testid="setting-layer-height-tooltip"
                      className="max-w-xs bg-[#141519] border-zinc-700 text-zinc-100 text-xs leading-relaxed"
                    >
                      Sets the height of the scrollable layer list in the original UI.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <span className="text-xs text-zinc-500 mono">{settings.layerListHeight}px</span>
              </div>
              <Slider
                data-testid="setting-layer-height"
                min={200}
                max={720}
                step={20}
                value={[settings.layerListHeight]}
                onValueChange={(v) => update({ layerListHeight: v[0] })}
              />
            </div>
            <Row label="Show palette strip" hint="Display the compact color band above the layer rows.">
              <Switch
                data-testid="setting-show-strip"
                checked={settings.showStrip}
                onCheckedChange={(v) => update({ showStrip: v })}
              />
            </Row>
          </SettingsSection>

          <SettingsSection
            icon={<Image className="h-4 w-4" />}
            title="Map previews"
            description="Adjust the appearance of every 2D map canvas."
          >
            <Row label="Rounded map previews" hint="Apply rounded corners to all height and color map canvases.">
              <Switch
                data-testid="setting-rounded-map-preview"
                checked={!!settings.roundedMapPreview}
                onCheckedChange={(v) => update({ roundedMapPreview: v })}
              />
            </Row>
          </SettingsSection>

          <SettingsSection
            icon={<FlaskConical className="h-4 w-4" />}
            title="Experimental tools"
            description="Optional tools for importing and authoring material patterns."
          >
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div data-testid="setting-import-color-map-row">
                    <Row
                      label="Import existing color map"
                      badge="Experimental"
                      hint="Adds the Import Color Map button to the active toolbar."
                    >
                      <Switch
                        data-testid="setting-show-import-color-map"
                        checked={settings.showImportColorMap}
                        onCheckedChange={(v) => update({ showImportColorMap: v })}
                      />
                    </Row>
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="left"
                  data-testid="setting-import-color-map-tooltip"
                  className="bg-[#141519] border-zinc-700 text-amber-200 text-xs"
                >
                  Experimental
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="rounded-xl border border-zinc-800/70 bg-[#111217] p-4">
              <PatternMaker
                patterns={customPatterns}
                onSave={onSavePattern}
                onDelete={onDeletePattern}
              />
            </div>
          </SettingsSection>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SettingsSection = ({ icon, title, description, children }) => (
  <section className="overflow-hidden rounded-2xl border border-zinc-800/80 bg-[#0c0d11]">
    <div className="flex items-start gap-3 border-b border-zinc-800/70 bg-zinc-900/35 px-4 py-3.5">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-rose-400/20 bg-rose-500/10 text-rose-300">
        {icon}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-zinc-100">{title}</h3>
        <p className="mt-0.5 text-[11px] leading-relaxed text-zinc-500">{description}</p>
      </div>
    </div>
    <div className="space-y-3 p-3">{children}</div>
  </section>
);

const Row = ({ label, badge, hint, children }) => (
  <div className="flex items-start justify-between gap-4 rounded-xl border border-zinc-800/70 bg-[#111217] p-4">
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-zinc-100">
        <span>{label}</span>
        {badge && (
          <span className="rounded-full border border-amber-400/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-300">
            {badge}
          </span>
        )}
      </div>
      <div className="mt-0.5 text-xs text-zinc-500">{hint}</div>
    </div>
    <div className="shrink-0 pt-1">{children}</div>
  </div>
);
